import type { OrderDto } from '@pl/domain';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { CHANNEL_DOT, channelName } from '../../lib/format';
import { useOrders, useSkuMap } from '../../lib/queries';
import { Bar, Empty, ErrorNote, RefetchNote, SkuTag, Spinner } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { useScanSite } from '../useScanSite';
import s from './ShipList.module.css';

/** What a worker calls this order across a dock. The API shows `code` when there is no ref. */
export const orderRef = (order: { externalRef: string | null; code?: string | null; id: string }): string => order.externalRef ?? order.code ?? order.id;

/** Every line scanned to its full quantity — the only state in which confirming is allowed. */
export const isComplete = (order: Pick<OrderDto, 'lines'>): boolean => order.lines.every((line) => line.picked + line.shipped >= line.qty);

/**
 * The orders this site still has to get onto a truck.
 *
 * The ones that are finished float to the top with the words that say what to do next, because
 * that is the only card on this screen carrying an action rather than a job. Everything else keeps
 * the order the server sent, which is by required-by date: the list is not re-ranked on the phone,
 * so two workers looking at two phones are looking at the same list.
 */
export function ShipList() {
  const { site } = useScanSite();
  // Unscoped until the site is known, or the phone fires a cross-site fetch and throws it away.
  const orders = useOrders({ siteId: site?.id, status: ['open', 'picking'] }, Boolean(site?.id));
  const skuById = useSkuMap();

  const rows = useMemo(() => {
    const all = orders.data ?? [];
    // A `picking` order with every line scanned is ready; an `open` one has nothing scanned yet
    // and so can never be. Partition rather than sort, so the rest keep the server's order exactly.
    const ready = all.filter((o) => o.status === 'picking' && isComplete(o));
    const readyIds = new Set(ready.map((o) => o.id));
    return [...ready, ...all.filter((o) => !readyIds.has(o.id))];
  }, [orders.data]);

  // The query above never runs without a site, so its "pending" would be a spinner forever.
  if (!site) {
    return (
      <div className={`scan-body ${s.listBody}`}>
        <Empty title="No site on this account" description="Shipping works from a site. Ask an admin to add you to one." />
      </div>
    );
  }

  if (orders.isPending) {
    return (
      <div className={`scan-body ${s.listBody}`}>
        <div className={s.listLoading}>
          <Spinner label="Loading orders" />
        </div>
      </div>
    );
  }

  // The list refreshes every 15 seconds. A refresh that fails keeps the orders already on screen,
  // with a RefetchNote above them; only a list that never loaded takes the screen.
  if (orders.isLoadingError) {
    return (
      <div className={`scan-body ${s.listBody}`}>
        <div className={s.listFail}>
          <ErrorNote error={orders.error} />
          <button type="button" className={`bigbtn ghost ${s.listRetry}`} onClick={() => void orders.refetch()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`scan-body ${s.listBody}`}>
      <RefetchNote queries={[orders]} />
      <div className="sect">
        To ship<span className="r">By required-by date</span>
      </div>
      {rows.length === 0 ? (
        <Empty title="Nothing to ship" description={`No open orders at ${site.name}.`} />
      ) : (
        <div className={s.orderList}>
          {rows.map((order) => {
            const ready = order.status === 'picking' && isComplete(order);
            return (
              <Link key={order.id} to={`/scan/ship/${order.id}`} className={`${s.orderCard}${ready ? ` ${s.orderReady}` : ''}`}>
                <span className={s.cardTop}>
                  <span className={`mono ${s.orderRef}`}>{orderRef(order)}</span>
                  {ready ? (
                    <span className={`chip ok ${s.readyChip}`}>
                      <Icon name="check" size={13} />
                      Ready: confirm shipped
                    </span>
                  ) : (
                    <span className="chip">{order.status === 'picking' ? 'Picking' : 'Open'}</span>
                  )}
                </span>

                <span className={s.cardMeta}>
                  <span className={`channel ${s.cardWho}`}>
                    <i style={{ background: CHANNEL_DOT[order.channel] }} />
                    {channelName(order.channel)}
                  </span>
                  <span className={s.cardCustomer}>{order.customerName}</span>
                  <span className={`mono ${s.cardCount}`}>
                    {order.scanned}
                    <span>/{order.totalQty}</span>
                  </span>
                </span>

                <Bar value={order.scanned} max={order.totalQty} tone={ready ? 'ok' : undefined} />

                <span className={s.lineChips}>
                  {order.lines.map((line) => (
                    <span key={line.id} className={`chip ${s.lineChip}`}>
                      <SkuTag sku={skuById.get(line.skuId)} short />
                      <span className={`mono ${s.lineQty}`}>×{line.qty}</span>
                    </span>
                  ))}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
